// Backend wraps end-of-turn tool outputs in `<system>...</system>` even
// when the content wasn't trimmed — strip before rendering.
const BACKEND_SYSTEM_ENVELOPE_RE = /\n?<system>[\s\S]*?<\/system>/g;

// Cap before splitting so a 500KB blob doesn't allocate a huge line array.
export const MAX_PREVIEW_CHARS = 200_000;

export function clampChars(text: string): string {
  return text.length > MAX_PREVIEW_CHARS
    ? text.slice(0, MAX_PREVIEW_CHARS)
    : text;
}

// `{n:6d}\t{line}` and `{n:>4}\t{line}` both match.
const CAT_N_LINE_RE = /^(\s*\d+)\t(.*)$/;

export interface NumberedLine {
  lineNumber?: number;
  content: string;
}

export interface ParsedToolResult {
  cleanText: string;
  /** Trailing `...` is the only reliable trim signal — `<system>` is added
   * even when content was untouched. */
  wasTruncated: boolean;
}

export interface ParsedFileRead extends ParsedToolResult {
  lines: NumberedLine[];
  hasNumberedLines: boolean;
}

export function stripSystemEnvelope(text: string): string {
  return text.replace(BACKEND_SYSTEM_ENVELOPE_RE, '');
}

export function parseToolResult(
  raw: string,
  opts?: { trimEnd?: boolean }
): ParsedToolResult {
  const stripped = stripSystemEnvelope(raw);
  const trimmed = opts?.trimEnd ? stripped.trimEnd() : stripped;
  const cleanText = clampChars(trimmed);
  return { cleanText, wasTruncated: cleanText.endsWith('...') };
}

export function parseFileRead(raw: string): ParsedFileRead {
  const base = parseToolResult(raw, { trimEnd: true });
  const lines: NumberedLine[] = [];
  let hasNumberedLines = false;
  for (const line of base.cleanText.split('\n')) {
    const match = CAT_N_LINE_RE.exec(line);
    if (match) {
      hasNumberedLines = true;
      const lineNumber = parseInt(match[1], 10);
      let content = match[2];
      // Peel a double-numbered prefix (`953\t953\tactual`) when the inner
      // number matches the outer — e.g. captured shell snapshots of an
      // already-numbered file get wrapped a second time on read. Only peel
      // on a number match so legitimate `\s*\d+\t...` content survives.
      const inner = CAT_N_LINE_RE.exec(content);
      if (inner && parseInt(inner[1], 10) === lineNumber) {
        content = inner[2];
      }
      lines.push({ lineNumber, content });
    } else {
      lines.push({ content: line });
    }
  }
  return { ...base, lines, hasNumberedLines };
}

export function fileReadFooter(
  parsed: ParsedFileRead,
  requestedLimit?: number
): string | null {
  if (!parsed.wasTruncated) return null;
  const numberedCount = parsed.lines.filter(
    (l) => l.lineNumber !== undefined
  ).length;
  const remaining =
    requestedLimit !== undefined
      ? Math.max(0, requestedLimit - numberedCount)
      : 0;
  return remaining > 0 ? `... ${remaining} more lines` : '... more lines';
}
